import { Organisation } from '@principle-theorem/principle-core';
import { type IOrganisation } from '@principle-theorem/principle-core/interfaces';
import { SortOrder } from '@principle-theorem/reporting/interfaces';
import { DocumentReference, type IReffable } from '@principle-theorem/shared';
import { compact, uniq } from 'lodash';

/**
 * Resolves the BigQuery dataset name for an organisation.
 *
 * Reads the explicit `bigqueryDataset` field. Throws if the field is not
 * set — every organisation must have an explicit dataset configured before
 * BigQuery operations can proceed. Use the backfill migration to populate
 * existing orgs.
 */
export function resolveBigQueryDataset(
  organisation: IReffable<IOrganisation>
): string {
  const org = organisation as IReffable<IOrganisation> & IOrganisation;
  if (org.bigqueryDataset) {
    return org.bigqueryDataset;
  }
  throw new Error(
    `Organisation ${organisation.ref.path} is missing a bigqueryDataset. ` +
      `Run the backfill-bigquery-dataset migration in principle-migrations to populate it.`
  );
}

/**
 * @deprecated Use `resolveBigQueryDataset` instead. This function derives the
 * dataset name from the Firestore document ID rather than reading the explicit
 * configuration stored on the organisation.
 */
export function toDatasetName(orgRef: DocumentReference<Organisation>): string {
  const prefix = 'org';
  return `${prefix}_${orgRef.id}`.toLowerCase();
}

export function getRowNumberAttribute(
  rowNumberAlias: string,
  uniqueBy: string = 'ref.referenceValue',
  orderBy?: string,
  orderByDirection: SortOrder = SortOrder.Descending
): string {
  const orderByProperty = uniq(compact([orderBy, 'timestamp.seconds']))
    .map((orderByProp) => `${orderByProp} ${orderByDirection}`)
    .join(', ');
  return `ROW_NUMBER() OVER(PARTITION BY ${uniqueBy} ORDER BY ${orderByProperty}) AS ${rowNumberAlias}`;
}
